import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { parseDay } from '@map-my-money/engine';
import { useSearchParams } from 'react-router';
import { PayoffChart } from '../components/charts/PayoffChart.tsx';
import { AmountInput, groupedAmount, ungroupedAmount } from '../components/AmountInput.tsx';
import { AskAssistant } from '../components/Assistant.tsx';
import { Icon } from '../components/Icon.tsx';
import { PageHeader } from '../components/Shell.tsx';
import { SyncStatus } from '../components/SyncStatus.tsx';
import { RateChangesNote } from '../components/RateChangesNote.tsx';
import { Field, Modal, Toggle } from '../components/ui.tsx';
import { t, type MessageKey } from '../i18n/index.ts';
import { todayDay, todayString } from '../lib/clock.ts';
import { compactMoney, fullDate, money, monthYear } from '../lib/format.ts';
import { useIsDesktop } from '../lib/hooks.ts';
import {
  debtPathOf, historyLines, itemOn, largestIncome, matchTerms, monthlyTotals, planFacts, rateSchedule, TAX_ACCOUNTS, taxReliefDefault, withDatedEdits,
  withoutChange, type ItemFacts,
} from '../lib/model.ts';
import {
  GROUPS, accountLabel, addSummary, amountWithFrequency, applyDraft, correctedText, datedEdits, datedEditsText, draftFrom, editSummary, frequencyLabel,
  fromError, historyFields, historyLineText, itemActive, itemLabel, itemTone, newItem, nextChangeText, planItems, rateScheduleText, removeItem,
  replaceItem, rowCells, typeLabel, validate,
  type Draft, type DraftErrors, type Group, type ItemKey, type NewKind, type PlanItem, type RateRow,
} from '../lib/planItems.ts';
import { usePlanNotice } from '../state/notices.ts';
import { useActivePlan, usePlan, useStore } from '../state/store.tsx';
import { useDocumentTitle } from '../lib/documentTitle.ts';
import type { MarkKind } from '../lib/marks.ts';
import { useMarks } from '../lib/useMarks.ts';

type Filter = 'all' | Group;

export function PlanScreen() {
  useDocumentTitle(t('nav.plan'));
  const wire = usePlan();
  const { savePlan, revision } = useStore();
  const desktop = useIsDesktop();
  const [params, setParams] = useSearchParams();
  const items = useMemo(() => planItems(wire), [wire]);
  const [filter, setFilter] = useState<Filter>('all');
  const [selected, setSelected] = useState<ItemKey | null>(() => (params.get('item') as ItemKey | null) ?? null);
  const [draftItem, setDraftItem] = useState<PlanItem | null>(null);

  useEffect(() => {
    const add = params.get('add');
    if (add === 'asset' || add === 'income' || add === 'expense' || add === 'investment' || add === 'debt') {
      const blank = newItem(add, todayString(), wire);
      const amount = params.get('amount');
      const item = amount && /^\d+(\.\d{1,2})?$/.test(amount) && blank.kind === 'tx' ? { ...blank, tx: { ...blank.tx, amount } } : blank;
      setDraftItem(item);
      setSelected(item.key);
      params.delete('add');
      params.delete('amount');
      setParams(params, { replace: true });
    }
  }, [params, setParams, wire]);

  const current = draftItem && draftItem.key === selected ? draftItem : items.find((i) => i.key === selected) ?? null;
  const isNew = current !== null && current === draftItem;

  const select = (key: ItemKey | null) => {
    setSelected(key);
    if (draftItem && key !== draftItem.key) setDraftItem(null);
  };
  const startNew = (kind: NewKind) => {
    const item = newItem(kind, todayString(), wire);
    setDraftItem(item);
    setSelected(item.key);
  };
  const toggleActive = (item: PlanItem, active: boolean) => {
    const after = applyDraft(item, { ...draftFrom(item), isActive: active });
    void savePlan(replaceItem(wire, after), editSummary(item, after, wire));
  };
  const save = async (item: PlanItem, after: PlanItem, from?: string) => {
    const today = todayString();
    const summary = isNew ? addSummary(after) : editSummary(item, after, wire, from === undefined ? undefined : { from, today });
    const ok = await savePlan(replaceItem(wire, after), summary, from !== undefined && after.kind === 'tx' ? { [after.tx.id]: from } : undefined);
    if (ok && isNew) setDraftItem(null);
    return ok;
  };
  const removeChange = async (item: PlanItem, from: string) => {
    if (item.kind !== 'tx') return;
    const after: PlanItem = { ...item, tx: withoutChange(item.tx, from) };
    await savePlan(replaceItem(wire, after), t('plan.history.removedSummary', { name: itemLabel(item), date: fullDate(parseDay(from)) }));
  };
  const remove = async (item: PlanItem) => {
    if (isNew) {
      select(null);
      return;
    }
    const ok = await savePlan(removeItem(wire, item), t('plan.summaryText.removed', { name: itemLabel(item) }));
    if (ok) select(null);
  };

  const visibleItems = filter === 'all' ? items : items.filter((i) => i.group === filter);
  const shared = { items, visibleItems, filter, setFilter, current, isNew, select, startNew, toggleActive, save, remove, removeChange, revision };
  return desktop ? <DesktopPlan {...shared} /> : <MobilePlan {...shared} />;
}

interface PlanProps {
  items: readonly PlanItem[];
  visibleItems: readonly PlanItem[];
  filter: Filter;
  setFilter: (f: Filter) => void;
  current: PlanItem | null;
  isNew: boolean;
  select: (key: ItemKey | null) => void;
  startNew: (kind: NewKind) => void;
  toggleActive: (item: PlanItem, active: boolean) => void;
  save: (item: PlanItem, after: PlanItem, from?: string) => Promise<boolean>;
  remove: (item: PlanItem) => Promise<void>;
  removeChange: (item: PlanItem, from: string) => Promise<void>;
  revision: number;
}

const NEW_KINDS: readonly { kind: NewKind; label: MessageKey }[] = [
  { kind: 'income', label: 'plan.newItem.income' },
  { kind: 'expense', label: 'plan.newItem.expense' },
  { kind: 'investment', label: 'plan.newItem.investment' },
  { kind: 'debt', label: 'plan.newItem.debt' },
  { kind: 'asset', label: 'plan.type.asset' },
];

function AddMenu({ onPick, compact = false }: { onPick: (kind: NewKind) => void; compact?: boolean }) {
  const { canEdit } = useActivePlan();
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  return (
    <div className="menu" ref={ref}>
      <button
        type="button"
        className={compact ? 'button button--sm button--primary' : 'button button--primary button--glow'}
        aria-expanded={open}
        aria-controls={menuId}
        disabled={!canEdit}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="plus" size={16} strokeWidth={2.5} />
        {t('plan.add')}
        <Icon name="chevronDown" size={14} strokeWidth={2.5} />
      </button>
      {open && (
        <ul className="menu__list" id={menuId} aria-label={t('plan.addMenu')}>
          {NEW_KINDS.map((k) => (
            <li key={k.kind}>
              <button type="button" className="menu__item" onClick={() => { setOpen(false); onPick(k.kind); }}>
                <span className={`dot dot--${k.kind}`} aria-hidden="true" />
                {t(k.label)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FilterTabs({ items, filter, setFilter }: Pick<PlanProps, 'items' | 'filter' | 'setFilter'>) {
  const options: { value: Filter; label: string }[] = [
    { value: 'all', label: t('plan.filter.all') },
    ...GROUPS.map((g) => ({ value: g.key, label: t(g.label) })),
  ];
  return (
    <div role="group" aria-label={t('plan.filter')} className="filter-chips">
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={filter === o.value} className="filter-chip" onClick={() => setFilter(o.value)}>
          {o.label}
          {o.value === 'all' && <span className="filter-chip__count">{items.length}</span>}
        </button>
      ))}
    </div>
  );
}

/** The monthly totals of what has started; what starts later is listed by the month it starts (R23). */
function MonthlyFooter() {
  const wire = usePlan();
  const totals = monthlyTotals(wire, todayDay());
  return (
    <p className="plan-footer" data-testid="plan-footer">
      {t('plan.summary')}: <strong>{t('plan.summary.in', { amount: money(totals.income) })}</strong> ·{' '}
      <strong>{t('plan.summary.out', { amount: money(totals.out) })}</strong> ·{' '}
      <strong className="text-violet">{t('plan.summary.invested', { amount: money(totals.invested) })}</strong>
      {totals.later.map((m) => {
        const parts = [
          m.income !== 0 ? t('plan.summary.laterIn', { amount: money(m.income) }) : null,
          m.out !== 0 ? t('plan.summary.laterOut', { amount: money(m.out) }) : null,
          m.invested !== 0 ? t('plan.summary.laterInvested', { amount: money(m.invested) }) : null,
        ].filter((p) => p !== null);
        return (
          <span key={m.from} className="plan-footer__later">
            {' · '}{t('plan.summary.from', { month: monthYear(m.from) })} {parts.join(', ')}
          </span>
        );
      })}
    </p>
  );
}

function DesktopPlan(props: PlanProps) {
  const wire = usePlan();
  const today = todayDay();
  const { waypoints } = useStore();
  const facts = useMemo(() => planFacts(wire, today, waypoints), [wire, today, waypoints]);
  const { visibleItems, current, select, toggleActive } = props;
  const { canEdit } = useActivePlan();
  const marks = useMarks();
  return (
    <div className="page plan-desktop">
      <PageHeader
        eyebrow={t('plan.eyebrow')}
        title={t('plan.title')}
        actions={<><SyncStatus /><AddMenu onPick={props.startNew} /></>}
      />
      <RateChangesNote />
      <FilterTabs items={props.items} filter={props.filter} setFilter={props.setFilter} />
      <div className="plan-desktop__grid">
        <section className="card card--bento plan-table-card" aria-label={t('plan.items')}>
          <div className="table-scroll">
            <table className="plan-table">
              <thead>
                <tr>
                  <th scope="col">{t('plan.col.name')}</th>
                  <th scope="col">{t('plan.col.type')}</th>
                  <th scope="col" className="num">{t('plan.col.amount')}</th>
                  <th scope="col">{t('plan.col.frequency')}</th>
                  <th scope="col" className="col-optional">{t('plan.col.starts')}</th>
                  <th scope="col">{t('plan.col.ends')}</th>
                  <th scope="col" className="col-optional">{t('plan.col.grows')}</th>
                  <th scope="col" className="num">{t('plan.col.status')}</th>
                </tr>
              </thead>
              <tbody>
                {visibleItems.map((item) => {
                  const cells = rowCells(item, facts.get(item.key), todayString());
                  const active = itemActive(item);
                  const isSelected = current?.key === item.key;
                  return (
                    <tr
                      key={item.key}
                      className={`plan-row ${isSelected ? 'plan-row--selected' : ''} ${active ? '' : 'plan-row--paused'}`}
                      onClick={() => select(item.key)}
                    >
                      <th scope="row">
                        <button type="button" className="plan-row__name" aria-pressed={isSelected} onClick={(e) => { e.stopPropagation(); select(item.key); }}>
                          {itemLabel(item)}<MarkTag item={item} marks={marks} />
                        </button>
                      </th>
                      <td><span className="type-tag"><span className={`dot dot--${itemTone(item)}`} aria-hidden="true" />{typeLabel(item)}</span></td>
                      <td className="num strong">
                        {item.kind === 'asset' ? compactMoney(cells.amount) : money(cells.amount)}
                        {cells.plannedFrom !== null && <span className="plan-row__planned">{t('plan.planned', { month: monthYear(cells.plannedFrom) })}</span>}
                        {cells.next !== null && <span className="plan-row__planned" data-testid="plan-row-next">{nextChangeText(cells.next)}</span>}
                      </td>
                      <td className="muted-2">{frequencyLabel(item)}</td>
                      <td className="muted-2 col-optional">{monthYear(cells.starts)}</td>
                      <td className={cells.ends === null ? 'muted' : cells.endsIsPayoff ? 'text-terracotta' : 'muted-2'}>
                        {cells.ends === null ? t('common.none') : monthYear(cells.ends)}
                      </td>
                      <td className={`col-optional ${cells.grows === null ? 'muted' : item.group === 'investing' || item.group === 'assets' ? 'text-violet' : 'muted-2'}`}>
                        {cells.grows ?? t('common.none')}
                      </td>
                      <td className="num" onClick={(e) => e.stopPropagation()}>
                        <Toggle checked={active} onChange={(v) => toggleActive(item, v)} label={t('plan.active', { name: itemLabel(item) })} disabled={!canEdit} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {visibleItems.length === 0 && <p className="muted plan-empty">{t('plan.empty')}</p>}
          </div>
          <MonthlyFooter />
        </section>
        <aside className="card card--pad-lg plan-detail" aria-label={current ? t('plan.detail', { name: itemLabel(current) }) : t('plan.detail.empty')}>
          {current ? (
            <ItemEditor key={`${current.key}-${props.revision}`} item={current} isNew={props.isNew} wire={wire} onSave={props.save} onDelete={props.remove}
              onRemoveChange={props.removeChange} onClose={() => select(null)} />
          ) : (
            <div className="plan-detail__empty">
              <Icon name="plan" size={22} />
              <p>{t('plan.detail.empty')}</p>
            </div>
          )}
        </aside>
      </div>
      <AskAssistant view="plan" />
    </div>
  );
}

/** A Start marker ("example", "check") until the item is edited. */
function MarkTag({ item, marks }: { item: PlanItem; marks: ReadonlyMap<string, MarkKind> }) {
  const mark = item.kind === 'tx' ? marks.get(item.tx.id) : undefined;
  if (!mark) return null;
  return (
    <span className={`badge plan-mark plan-mark--${mark}`} title={t(mark === 'example' ? 'plan.mark.exampleLabel' : 'plan.mark.checkLabel')} data-testid={`plan-mark-${mark}`}>
      {t(mark === 'example' ? 'plan.mark.example' : 'plan.mark.check')}
      <span className="sr-only">: {t(mark === 'example' ? 'plan.mark.exampleLabel' : 'plan.mark.checkLabel')}</span>
    </span>
  );
}

function MobilePlan(props: PlanProps) {
  const wire = usePlan();
  const today = todayDay();
  const { waypoints } = useStore();
  const facts = useMemo(() => planFacts(wire, today, waypoints), [wire, today, waypoints]);
  const { items, current, select } = props;
  const marks = useMarks();
  return (
    <div className="page page--mobile">
      <header className="mobile-header">
        <h1 className="mobile-title">{t('plan.title')}</h1>
        <AddMenu onPick={props.startNew} compact />
      </header>
      <RateChangesNote />
      {GROUPS.map((g) => {
        const groupItems = items.filter((i) => i.group === g.key);
        if (groupItems.length === 0) return null;
        return (
          <section key={g.key} className="stack-sm" aria-labelledby={`group-${g.key}`}>
            <h2 className="eyebrow" id={`group-${g.key}`}>{t(g.label)}</h2>
            <ul className="list-card">
              {groupItems.map((item) => {
                const cells = rowCells(item, facts.get(item.key), todayString());
                return (
                <li key={item.key}>
                  <button type="button" className={`list-row ${itemActive(item) ? '' : 'list-row--paused'}`} onClick={() => select(item.key)}>
                    <span className={`dot dot--${itemTone(item)}`} aria-hidden="true" />
                    <span className="list-row__text">
                      <span className="list-row__title">{itemLabel(item)}<MarkTag item={item} marks={marks} /></span>
                      <span className="list-row__meta">
                        {typeLabel(item)} · {cells.plannedFrom !== null ? t('plan.planned', { month: monthYear(cells.plannedFrom) }) : frequencyLabel(item)}
                        {cells.next !== null && <span className="list-row__next" data-testid="plan-row-next">{nextChangeText(cells.next)}</span>}
                      </span>
                    </span>
                    <span className="list-row__amount">
                      {item.kind === 'asset' ? compactMoney(cells.amount) : amountWithFrequency(itemOn(item.tx, todayString()))}
                    </span>
                    <Icon name="chevronRight" size={16} className="list-row__chevron" />
                  </button>
                </li>
                );
              })}
            </ul>
          </section>
        );
      })}
      <MonthlyFooter />
      <AskAssistant view="plan" />
      {current && (
        <Modal title={itemLabel(current)} variant="sheet" onClose={() => select(null)}>
          <ItemEditor key={`${current.key}-${props.revision}`} item={current} isNew={props.isNew} wire={wire} onSave={props.save} onDelete={props.remove}
            onRemoveChange={props.removeChange} onClose={() => select(null)} inSheet />
        </Modal>
      )}
    </div>
  );
}

const isDayText = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s);

const FREQUENCIES = ['oneTime', 'weekly', 'fortnightly', 'monthly', 'quarterly', 'yearly'] as const;

function ItemEditor({ item, isNew, wire, onSave, onDelete, onRemoveChange, onClose, inSheet = false }: {
  item: PlanItem;
  isNew: boolean;
  wire: ReturnType<typeof usePlan>;
  onSave: (item: PlanItem, after: PlanItem, from?: string) => Promise<boolean>;
  onDelete: (item: PlanItem) => Promise<void>;
  onRemoveChange: (item: PlanItem, from: string) => Promise<void>;
  onClose: () => void;
  inSheet?: boolean;
}) {
  const { canEdit } = useActivePlan();
  const rateNotice = usePlanNotice('rate-changes');
  const { waypoints } = useStore();
  const today = todayDay();
  const todayText = todayString();
  const savedFacts = useMemo(() => planFacts(wire, today, waypoints).get(item.key) ?? null, [wire, today, waypoints, item.key]);
  const base = useMemo(() => draftFrom(item, savedFacts), [item, savedFacts]);
  const [draft, setDraft] = useState<Draft>(base);
  const [touched, setTouched] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const currentErrors = validate(draft, todayText);
  const errors: DraftErrors = touched ? currentErrors : {};
  const idp = useId();
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((d) => ({ ...d, [key]: value }));
  const dirty = isNew || JSON.stringify(draft) !== JSON.stringify(base);
  const tx = item.kind === 'tx' ? item.tx : null;
  const type = tx?.type ?? 'asset';
  const id = tx?.id ?? '';
  const linked = tx?.linkedAssetId ? (wire.assets ?? []).find((a) => a.id === tx.linkedAssetId) : undefined;
  const byBalance = tx?.type === 'debt' && tx.debtEntryMode === 'currentBalance';

  const history = useMemo(() => (tx && !isNew ? historyLines(tx, todayText) : []), [tx, isNew, todayText]);
  const pendingFrom = [...history].reverse().find((line) => line.when === 'future')?.from ?? null;
  const [from, setFrom] = useState(pendingFrom ?? todayText);
  const [pickingFrom, setPickingFrom] = useState(false);
  const applied = useMemo(() => applyDraft(item, draft, savedFacts), [item, draft, savedFacts]);
  const edits = isNew ? [] : datedEdits(item, applied);
  const startDay = applied.kind === 'tx' ? applied.tx.startDate : '';
  const dated = edits.length > 0 && startDay < todayText;
  const correction = dated && from <= startDay;
  const fromProblem = dated ? fromError(from, pendingFrom) : null;

  // The editor's numbers follow the draft as it is typed, read from the plan with the draft in it, dated as it will be saved.
  const valid = Object.keys(currentErrors).length === 0 && fromProblem === null;
  const preview = useMemo(
    () => (valid ? withDatedEdits(replaceItem(wire, applied), wire, todayText, dated && tx ? { [tx.id]: from } : {}) : null),
    [valid, wire, applied, todayText, dated, tx, from],
  );
  const previewWire = preview ?? (isNew ? replaceItem(wire, item) : wire);
  const facts: ItemFacts | null = useMemo(() => planFacts(previewWire, today, waypoints).get(item.key) ?? null, [previewWire, today, waypoints, item.key]);
  const path = useMemo(() => (type === 'debt' ? debtPathOf(previewWire, id, today) : null), [type, previewWire, id, today]);
  const schedule = useMemo(() => (type === 'debt' ? rateSchedule(previewWire, id, today) : null), [type, previewWire, id, today]);
  const terms = useMemo(() => (type === 'investment' ? matchTerms(previewWire, id, today) : null), [type, previewWire, id, today]);
  const largest = useMemo(() => largestIncome(wire, today), [wire, today]);
  const incomes = (wire.transactions ?? []).filter((x) => x.type === 'income');
  const accounts = [...TAX_ACCOUNTS[wire.configuration.taxJurisdiction === 'us' ? 'us' : 'uk']];
  if (draft.taxAccount !== '' && !accounts.includes(draft.taxAccount as (typeof accounts)[number])) accounts.push(draft.taxAccount as (typeof accounts)[number]);

  const submit = async () => {
    setTouched(true);
    if (!valid) return;
    setBusy(true);
    const ratesChanged = JSON.stringify(draft.rateChanges) !== JSON.stringify(base.rateChanges);
    const ok = await onSave(item, applied, dated ? from : undefined);
    if (ok && ratesChanged) rateNotice.dismiss();
    setBusy(false);
  };

  const setBalance = (value: string) =>
    setDraft((d) => ({ ...d, balance: value, balanceDate: d.balanceDate === base.balanceDate ? todayText : d.balanceDate }));
  const setRateRow = (index: number, row: RateRow) => set('rateChanges', draft.rateChanges.map((r, i) => (i === index ? row : r)));

  const field = (key: Exclude<keyof Draft, 'rateChanges' | 'isActive'>, label: MessageKey, opts: { inputMode?: 'decimal'; type?: string; hint?: string; placeholder?: string; onChange?: (value: string) => void } = {}) => (
    <Field label={t(label)} htmlFor={`${idp}-${key}`} error={errors[key] ?? null} {...(opts.hint ? { hint: opts.hint } : {})}>
      <input
        id={`${idp}-${key}`}
        className="input"
        type={opts.type ?? 'text'}
        inputMode={opts.inputMode}
        placeholder={opts.placeholder}
        value={draft[key]}
        onChange={(e) => (opts.onChange ?? ((v: string) => set(key, v)))(e.target.value)}
        aria-invalid={errors[key] ? true : undefined}
      />
    </Field>
  );

  const moneyField = (key: 'amount' | 'balance' | 'initialBalance', label: MessageKey, onChange: (value: string) => void = (v) => set(key, v)) => (
    <Field label={t(label)} htmlFor={`${idp}-${key}`} error={errors[key] ?? null}>
      <AmountInput
        id={`${idp}-${key}`}
        className="input"
        value={groupedAmount(draft[key])}
        onChange={(text) => onChange(ungroupedAmount(text))}
        aria-invalid={errors[key] ? true : undefined}
      />
    </Field>
  );

  const scheduleText = schedule && schedule.changes.length > 0 ? rateScheduleText(schedule) : null;
  const shownFields = historyFields(history);

  const fromControl = dated && (
    <div className="from-line" data-testid="from-line" aria-live="polite">
      <p className="from-line__text">
        {correction ? (
          <>{t('plan.from.always', { values: correctedText(edits) })}</>
        ) : (
          <>
            <strong>{itemLabel(applied)}</strong> {datedEditsText(edits)}
            <span className="from-line__sep" aria-hidden="true"> · </span>
            {from === todayText ? t('plan.from.today', { date: fullDate(parseDay(from)) }) : t('plan.from.on', { date: isDayText(from) ? fullDate(parseDay(from)) : from })}
          </>
        )}
        {!pickingFrom && (
          <>
            <span className="from-line__sep" aria-hidden="true"> · </span>
            <button type="button" className="link-button" onClick={() => setPickingFrom(true)}>{t('plan.from.changeDate')}</button>
          </>
        )}
      </p>
      {correction && <p className="field__hint">{t('plan.from.alwaysHint')}</p>}
      {pickingFrom && (
        <div className="from-line__pick">
          <Field label={t('plan.from.label')} htmlFor={`${idp}-from`} error={fromProblem}>
            <input id={`${idp}-from`} className="input" type="date" value={from} min={pendingFrom ?? undefined}
              aria-invalid={fromProblem ? true : undefined} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          {pendingFrom === null && !correction && (
            <button type="button" className="button button--sm button--ghost" onClick={() => setFrom(startDay)}>{t('plan.from.start')}</button>
          )}
          {from !== (pendingFrom ?? todayText) && (
            <button type="button" className="button button--sm button--ghost" onClick={() => setFrom(pendingFrom ?? todayText)}>
              {pendingFrom === null ? t('plan.from.backToToday') : t('plan.from.backToChange')}
            </button>
          )}
        </div>
      )}
    </div>
  );

  const historyList = tx && history.length > 1 && (
    <section className="dated-history" aria-labelledby={`${idp}-history`} data-testid="item-history">
      <h3 className="field__label" id={`${idp}-history`}>{t('plan.history.title')}</h3>
      <ul className="dated-history__list">
        {history.map((line) => (
          <li key={line.from ?? 'start'} className={`dated-history__line dated-history__line--${line.when}`}>
            <span className="dated-history__text">{historyLineText(tx, line, shownFields)}</span>
            {line.when === 'current' && <span className="dated-history__tag">{t('plan.history.now')}</span>}
            {line.when === 'future' && line.from !== null && canEdit && !dirty && (
              <button type="button" className="link-button dated-history__remove" disabled={busy}
                aria-label={t('plan.history.removeLabel', { date: fullDate(parseDay(line.from)) })}
                onClick={() => {
                  setBusy(true);
                  void onRemoveChange(item, line.from!).finally(() => setBusy(false));
                }}>
                {t('plan.history.remove')}
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
  const matchPercent = terms?.matchPercent ?? 0;

  return (
    <form className="item-editor" onSubmit={(e) => { e.preventDefault(); void submit(); }} noValidate>
      {!inSheet && (
        <div className="item-editor__head">
          <span className={`item-editor__icon item-editor__icon--${itemTone(item)}`} aria-hidden="true">
            <Icon name={type === 'debt' ? 'car' : type === 'income' ? 'trendUp' : type === 'investment' ? 'bars' : type === 'asset' ? 'home' : 'receipt'} size={16} />
          </span>
          <h2 className="item-editor__title">{itemLabel(item) || t('plan.field.name')}</h2>
          <button type="button" className="icon-button" aria-label={t('plan.detail.close')} onClick={onClose}>
            <Icon name="close" size={14} />
          </button>
        </div>
      )}

      {field('label', 'plan.field.name')}
      {byBalance && (
        <div className="field-row">
          {moneyField('balance', 'plan.field.balanceAmount', setBalance)}
          {field('balanceDate', 'plan.field.balanceOn', { type: 'date' })}
        </div>
      )}
      {facts?.kind === 'debt' && (!byBalance || draft.balanceDate !== todayText) && (
        <div className="readout" data-testid="debt-balance-today">
          <span className="readout__label">{t('plan.field.balance')}</span>
          <span className="readout__value text-terracotta">{money(facts.balanceToday)}</span>
        </div>
      )}
      {facts?.kind === 'investment' && (
        <div className="readout">
          <span className="readout__label">{t('plan.field.value')}</span>
          <span className="readout__value text-violet">{money(facts.valueToday)}</span>
        </div>
      )}
      {facts?.kind === 'asset' && (
        <div className="readout" data-testid="asset-readout">
          <span className="readout__label">{facts.plannedFrom !== null ? t('plan.field.plannedFor') : t('plan.field.value')}</span>
          <span className="readout__value text-violet">{facts.plannedFrom !== null ? monthYear(facts.plannedFrom) : money(facts.valueToday)}</span>
        </div>
      )}
      <div className="field-row">
        {type === 'asset'
          ? moneyField('amount', 'plan.field.purchasePrice')
          : moneyField('amount', type === 'debt' ? 'plan.field.payment' : 'plan.field.amount')}
        {type === 'debt' || type === 'investment'
          ? field('rate', type === 'debt' ? 'plan.field.rate' : 'plan.field.return', { inputMode: 'decimal' })
          : type === 'asset'
            ? item.kind === 'asset' && item.asset.valueTrajectory.kind === 'appreciating'
              ? field('growth', 'plan.field.appreciation', { inputMode: 'decimal' })
              : null
            : field('growth', 'plan.field.grows', { inputMode: 'decimal' })}
      </div>
      {type !== 'investment' && fromControl}
      {type !== 'investment' && historyList}
      {type !== 'asset' && (
        <Field label={t('plan.field.frequency')} htmlFor={`${idp}-freq`}>
          <select id={`${idp}-freq`} className="input" value={draft.frequency} onChange={(e) => set('frequency', e.target.value)}>
            {FREQUENCIES.map((f) => <option key={f} value={f}>{t(`plan.freq.${f}` as MessageKey)}</option>)}
            {draft.frequency === 'custom' && <option value="custom">{t('plan.freq.custom')}</option>}
          </select>
        </Field>
      )}
      <div className="field-row">
        {field('startDate', type === 'asset' ? 'plan.field.purchaseDate' : 'plan.field.starts', { type: 'date' })}
        {type !== 'asset' && type !== 'debt' && field('endDate', 'plan.field.ends', { type: 'date' })}
        {type === 'debt' && (
          <div className="readout readout--field">
            <span className="readout__label">{t('plan.col.ends')}</span>
            <span className="readout__value text-terracotta">{facts?.kind === 'debt' && facts.payoff ? monthYear(facts.payoff) : t('common.none')}</span>
          </div>
        )}
      </div>
      {type === 'debt' && (
        <fieldset className="rate-schedule" aria-describedby={scheduleText ? `${idp}-rates-text` : undefined}>
          <legend className="field__label">{t('plan.rates.title')}</legend>
          {draft.rateChanges.length === 0 && <p className="field__hint">{t('plan.rates.none')}</p>}
          {draft.rateChanges.map((row, i) => {
            const rowErrors = errors.rateChanges?.[i] ?? null;
            return (
              <div key={i} className="rate-schedule__row" data-testid="rate-row">
                <Field label={t('plan.rates.date')} htmlFor={`${idp}-rate-date-${i}`} error={rowErrors?.date ?? null}>
                  <input id={`${idp}-rate-date-${i}`} className="input" type="date" value={row.date} aria-invalid={rowErrors?.date ? true : undefined}
                    onChange={(e) => setRateRow(i, { ...row, date: e.target.value })} />
                </Field>
                <Field label={t('plan.rates.rate')} htmlFor={`${idp}-rate-rate-${i}`} error={rowErrors?.rate ?? null}>
                  <input id={`${idp}-rate-rate-${i}`} className="input" inputMode="decimal" value={row.rate} aria-invalid={rowErrors?.rate ? true : undefined}
                    onChange={(e) => setRateRow(i, { ...row, rate: e.target.value })} />
                </Field>
                <button type="button" className="icon-button rate-schedule__remove" aria-label={t('plan.rates.remove', { n: i + 1 })}
                  onClick={() => set('rateChanges', draft.rateChanges.filter((_, j) => j !== i))}>
                  <Icon name="trash" size={14} />
                </button>
              </div>
            );
          })}
          <button type="button" className="button button--sm button--ghost rate-schedule__add" onClick={() => set('rateChanges', [...draft.rateChanges, { date: '', rate: '' }])}>
            <Icon name="plus" size={14} strokeWidth={2.5} />
            {t('plan.rates.add')}
          </button>
          {scheduleText && <p className="note rate-schedule__text" id={`${idp}-rates-text`} data-testid="rate-schedule-text">{scheduleText}</p>}
        </fieldset>
      )}
      {type === 'investment' && moneyField('initialBalance', 'plan.field.startBalance')}
      {type === 'investment' && field('employerMatch', 'plan.field.employerMatch', { inputMode: 'decimal' })}
      {type === 'investment' && fromControl}
      {type === 'investment' && historyList}
      {type === 'investment' && (matchPercent > 0 || draft.matchIncomeId !== '') && (
        <Field label={t('plan.field.matchIncome')} htmlFor={`${idp}-match-income`}>
          <select id={`${idp}-match-income`} className="input" value={draft.matchIncomeId} onChange={(e) => set('matchIncomeId', e.target.value)}>
            <option value="">{largest ? t('plan.match.largest', { name: largest.label }) : t('plan.match.largestNone')}</option>
            {incomes.map((income) => <option key={income.id} value={income.id}>{income.label ?? income.id}</option>)}
          </select>
        </Field>
      )}
      {type === 'investment' && matchPercent > 0 && (
        <p className="field__hint" data-testid="match-terms">
          {terms?.income
            ? terms.ownPercent !== null
              ? t('plan.match.terms', { own: terms.ownPercent, match: terms.matchPercent, income: terms.income.label })
              : t('plan.match.termsMatchOnly', { match: terms.matchPercent, income: terms.income.label })
            : t('plan.match.noIncome')}
        </p>
      )}
      {type === 'investment' && (
        <div className="field-row">
          <Field label={t('plan.field.taxAccount')} htmlFor={`${idp}-tax-account`}>
            <select id={`${idp}-tax-account`} className="input" value={draft.taxAccount} onChange={(e) => set('taxAccount', e.target.value)}>
              <option value="">{t('plan.tax.none')}</option>
              {accounts.map((a) => <option key={a} value={a}>{accountLabel(a)}</option>)}
            </select>
          </Field>
          {draft.taxAccount !== '' && field('taxRelief', 'plan.field.taxRelief', {
            inputMode: 'decimal',
            placeholder: String(taxReliefDefault(draft.taxAccount as (typeof accounts)[number])),
            hint: t('plan.tax.default', { pct: taxReliefDefault(draft.taxAccount as (typeof accounts)[number]) }),
          })}
        </div>
      )}
      {type === 'debt' && path && path.length > 1 && (
        <div className="stack-xs">
          <span className="field__label">{t('plan.field.payoff')}</span>
          <PayoffChart
            path={path}
            label={facts?.kind === 'debt' && facts.payoff
              ? t('plan.field.payoffChart', { name: itemLabel(item), from: money(facts.balanceToday), date: monthYear(facts.payoff) })
              : t('plan.field.payoffNever')}
          />
        </div>
      )}
      {linked && (
        <p className="note">
          <Icon name="alert" size={16} />
          {t('plan.linkedNote', { asset: (linked.label ?? linked.id).toLowerCase() })}
        </p>
      )}
      <Toggle checked={draft.isActive} onChange={(v) => set('isActive', v)} label={t('plan.field.active')} visibleLabel={<span>{t('plan.field.active')}</span>} />
      <div className="item-editor__actions">
        {confirming ? (
          <button type="button" className="button button--danger" onClick={() => void onDelete(item)}>
            {t('plan.deleteConfirm', { name: itemLabel(item) })}
          </button>
        ) : (
          <button type="button" className="button button--ghost" disabled={!canEdit && !isNew} onClick={() => (isNew ? onClose() : setConfirming(true))}>
            {isNew ? t('common.cancel') : t('common.delete')}
          </button>
        )}
        <button type="submit" className="button button--primary" disabled={busy || !dirty || !canEdit}>
          {t('common.saveChanges')}
        </button>
      </div>
    </form>
  );
}
